import Chart from 'react-apexcharts';
import { useE3HistoricalData } from '@src/modules/performance/context/reports-api-hooks';
import { Text, Wrap } from '@chakra-ui/react';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Trends',
  content: [
    {
      title: 'Value for Money',
      description:
  <Text fontSize="inherit" mb={2}>
    The trend of the normalised position of the network compared to the list of peers, for Value for Money. Value for Money measures network condition against road renewal direct costs (resurfacing, rehabilitation, and kerb and channel drainage) in the last three years.
  </Text>,
      source: null,
    },
    {
      title: 'Efficiency',
      description:
  <Text fontSize="inherit" mb={2}>The trend of the normalised position of the network compared to the list of peers, for Efficiency. Efficiency measures the lane kms of road renewal work (resurfacing, rehabilitation, and kerb and channel drainage) done in the last three years against the road renewal direct costs for resurfacing, rehabilitation, and kerb and channel drainage in the same period.</Text>,
      source: null,
    },
    {
      title: 'Effectiveness',
      description:
  <Text fontSize="inherit" mb={2}>The trend of the normalised position of the network compared to the list of peers, for Effectiveness. Effectiveness measures network condition against the quantity of road renewal work done (resurfacing, rehabilitation, and kerb and channel drainage) in the last three years.</Text>,
      source:
  <Text fontSize="inherit" as="span">
    Calculated by Road Efficiency Group
  </Text>,
    },
  ],
};

const mapValueToName = (value) => {
  if (value <= 1.667) return 'Lower';
  if (value <= 2.333) return 'Medium';
  return 'Higher';
};

function E3Trends() {
  const { data: historicalData, isLoading: isLoadingHistoricalData } = useE3HistoricalData();

  const sortedHistoricalData = historicalData?.sort((a, b) => a.year - b.year);
  const years = [...new Set(sortedHistoricalData?.map((x) => x.year).flat())];

  const historicalLockYear = sortedHistoricalData?.at(-1)?.lockYear;

  const efficiencyData = [
    {
      name: 'Efficiency',
      type: 'line',
      data: years?.map((year) => ({
        x: year,
        y: sortedHistoricalData?.find((d) => d.year === year)?.efficiencyPlace || null,
      })),
      color: CHART_COLOURS.orange,
    },
  ];

  const economyData = [
    {
      name: 'Value for Money',
      data: years?.map((year) => ({
        x: year,
        y: sortedHistoricalData?.find((d) => d.year === year)?.economyPlace || null,
      })),
      color: CHART_COLOURS.orange,
    },
  ];

  const effectivenessData = [
    {
      name: 'Effectiveness',
      type: 'line',
      data: years?.map((year) => ({
        x: year,
        y: sortedHistoricalData?.find((d) => d.year === year)?.effectivenessPlace || null,
      })),
      color: CHART_COLOURS.orange,
    },
  ];

  return (
    <ReportSection
      title="Trends"
      lockYear={historicalLockYear}
      isLoading={isLoadingHistoricalData}
      informationSource={informationSource}
    >
      <Wrap>
        <Chart
          options={{
            title: {
              text: 'Value for Money',
            },
            chart: {
              animations: {
                enabled: false,
              },
              toolbar: {
                export: {
                  csv: {
                    valueFormatter(value) {
                      return mapValueToName(value);
                    },
                  },
                },
              },
            },
            xaxis: {
              categories: years || [],
            },
            yaxis: [
              {
                min: 1,
                max: 3,
                tickAmount: 12,
                forceNiceScale: false,
                decimalsInFloat: 3,
                title: {
                  text: '',
                },
                labels: {
                  formatter(val) {
                    if (val.toFixed(3) === '1.333') return 'Lower';
                    if (val.toFixed(3) === '2.000') return 'Medium';
                    if (val.toFixed(3) === '2.667') return 'Higher';
                    return '';
                  },
                },
              },
            ],
            grid: {
              yaxis: {
                lines: {
                  show: false,
                },
              },
              padding: {
                left: 20,
              },
            },
            dataLabels: {
              enabled: false,
            },
            legend: {
              show: false,
            },
            tooltip: {
              enabled: false,
            },
            annotations: {
              yaxis: [
                {
                  y: 1.667,
                  borderColor: '#ccc',
                  strokeDashArray: 3,
                },
                {
                  y: 2.333,
                  borderColor: '#ccc',
                  strokeDashArray: 3,
                },
                {
                  y: 3.000,
                  borderColor: '#ccc',
                  strokeDashArray: 0,
                },
              ],
            },
          }}
          series={economyData}
          type="line"
          width="100%"
          height="240"
        />
        <Chart
          options={{
            title: {
              text: 'Efficiency',
            },
            chart: {
              animations: {
                enabled: false,
              },
              toolbar: {
                export: {
                  csv: {
                    valueFormatter(value) {
                      return mapValueToName(value);
                    },
                  },
                },
              },
            },
            xaxis: {
              categories: years || [],
            },
            yaxis: [
              {
                min: 1,
                max: 3,
                tickAmount: 12,
                forceNiceScale: false,
                decimalsInFloat: 3,
                title: {
                  text: '',
                },
                labels: {
                  formatter(val) {
                    if (val.toFixed(3) === '1.333') return 'Lower';
                    if (val.toFixed(3) === '2.000') return 'Medium';
                    if (val.toFixed(3) === '2.667') return 'Higher';
                    return '';
                  },
                },
              },
            ],
            grid: {
              yaxis: {
                lines: {
                  show: false,
                },
              },
              padding: {
                left: 20,
              },
            },
            dataLabels: {
              enabled: false,
            },
            legend: {
              show: false,
            },
            tooltip: {
              enabled: false,
            },
            annotations: {
              yaxis: [
                {
                  y: 1.667,
                  borderColor: '#ccc',
                  strokeDashArray: 3,
                },
                {
                  y: 2.333,
                  borderColor: '#ccc',
                  strokeDashArray: 3,
                },
                {
                  y: 3.000,
                  borderColor: '#ccc',
                  strokeDashArray: 0,
                },
              ],
            },
          }}
          series={efficiencyData}
          type="line"
          width="100%"
          height="240"
        />
        <Chart
          options={{
            title: {
              text: 'Effectiveness',
            },
            chart: {
              animations: {
                enabled: false,
              },
              toolbar: {
                export: {
                  csv: {
                    valueFormatter(value) {
                      return mapValueToName(value);
                    },
                  },
                },
              },
            },
            xaxis: {
              categories: years || [],
            },
            yaxis: [
              {
                min: 1,
                max: 3,
                tickAmount: 12,
                forceNiceScale: false,
                decimalsInFloat: 3,
                title: {
                  text: '',
                },
                labels: {
                  formatter(val) {
                    if (val.toFixed(3) === '1.333') return 'Lower';
                    if (val.toFixed(3) === '2.000') return 'Medium';
                    if (val.toFixed(3) === '2.667') return 'Higher';
                    return '';
                  },
                },
              },
            ],
            grid: {
              yaxis: {
                lines: {
                  show: false,
                },
              },
              padding: {
                left: 20,
              },
            },
            dataLabels: {
              enabled: false,
            },
            legend: {
              show: false,
            },
            tooltip: {
              enabled: false,
            },
            annotations: {
              yaxis: [
                {
                  y: 1.667,
                  borderColor: '#ccc',
                  strokeDashArray: 3,
                },
                {
                  y: 2.333,
                  borderColor: '#ccc',
                  strokeDashArray: 3,
                },
                {
                  y: 3.000,
                  borderColor: '#ccc',
                  strokeDashArray: 0,
                },
              ],
            },
          }}
          series={effectivenessData}
          type="line"
          width="100%"
          height="240"
        />
      </Wrap>
    </ReportSection>
  );
}

export default E3Trends;
